import { useEffect } from 'react';

interface UseEscapeKeyOptions {
  /**
   * 훅 활성화 여부
   * 일반적으로 Popover가 열려있을 때만 활성화
   */
  enabled: boolean;

  /**
   * ESC 키 입력 시 호출될 콜백 함수
   */
  onEscape: () => void;
}

/**
 * ESC 키 입력을 감지하여 콜백을 실행하는 커스텀 훅
 *
 * @description
 * Popover나 Modal이 열려있을 때 ESC 키를 눌러 닫을 수 있도록 지원합니다.
 *
 * @example
 * ```tsx
 * // Popover가 열려있을 때만 ESC 키 활성화
 * useEscapeKey({
 *   onEscape: closePopover,
 *   enabled: isOpen,
 * });
 * ```
 *
 * @param options - ESC 키 핸들러 옵션
 */
export const useEscapeKey = ({ onEscape, enabled }: UseEscapeKeyOptions) => {
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onEscape();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onEscape, enabled]);
};
